import React, { useEffect, useState } from 'react';
import './web-coin-shop-card.css';
import { useTranslations } from 'next-intl';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import {
  avatarFrameRender,
  borderStyleRenderer,
  onCommonRender,
} from './render';
import { Button } from '@wwe-portal/ui/components/button/button';
import { ClickableDiv } from '@wwe-portal/ui/components/button/clickable-div';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons/icomoon';

import WebCoinShopBannerMobileWrapper from '../../offer-card-banner/offer-card-banner-mobile-wrapper';
import LoadAnimateButton from '../../load-animate-button';
import { tryFormatThousands } from '@wwe-portal/utils/format';

function WebCoinShopCardMobile({
  title,
  rarity = '',
  image,
  paymentLoading,
  name = '',
  price,
  id,
  unlocked,
  handlePurchaseClick,
  handlePurchaseEnergyClick,
  listType = '',
  data,
  showModal,
  quantity,
}: Readonly<{
  title: string;
  rarity: string;
  image: string;
  paymentLoading: boolean;
  name?: string;
  price: string | number;
  id: string | number;
  unlocked: boolean;
  handlePurchaseClick?: (price, id, listType, title) => void;
  handlePurchaseEnergyClick?: (name, price) => void;
  listType: string;
  data: any;
  showModal?: boolean;
  quantity?: number;
}>) {
  /**
   * Use translation hook
   * @see https://react.i18next.com/
   */
  const t = useTranslations();
  const { isTablet } = useResponsive();
  const [show, setShow] = useState(false);

  /**
   * Check if All necessary data exists
   * for calculating Bonus % and Strike-through price
   */
  const isDataExistsForStrikeAndBonus =
    data?.Price &&
    data?.defaultCurrency &&
    data?.Price?.[data.defaultCurrency] &&
    data?.DiscountPercentage &&
    data?.DiscountPercentage > 0;
  const strikeThroughPrice = isDataExistsForStrikeAndBonus
    ? data?.Price?.[data.defaultCurrency] / (1 - data?.DiscountPercentage / 100)
    : 0;

  useEffect(() => {
    if (!showModal) {
      setShow(false);
    }
  }, [showModal]);

  return (
    <>
      <WebCoinShopBannerMobileWrapper
        open={show}
        onClose={() => setShow(false)}
      >
        <div className="web-coin-offer-modal flex w-full flex-col items-center justify-center rounded-xl bg-[#272B4A] px-[15px] pb-5 pt-[30px]">
          <div className="web-coin-shop-card-image-back responsive-back relative flex">
            <div
              className="web-coin-shop-card-image-back-shine-block responsive-shine-block info-block"
              style={avatarFrameRender(true, rarity, true)}
            />
            <div
              className={`web-coin-shop-card-image-back-shine-block-image info-image responsive-shine-block-image${
                listType === 'frame' ? '-frame' : ''
              }`}
              style={
                listType === 'energy'
                  ? {
                      backgroundImage: `url(${image})`,
                      backgroundSize: isTablet ? 'contain' : '50%',
                    }
                  : { backgroundImage: `url(${image})` }
              }
            />
          </div>

          <p className="text-6 my-[10px] text-center font-bold">{title}</p>

          {listType !== 'energy' ? (
            <div>{onCommonRender(rarity)}</div>
          ) : (
            <div>
              <p
                className="text-text400 mb-0 text-xs font-semibold"
                style={{ marginTop: 7 }}
              >
                {quantity || 1} {t('left')}
              </p>
            </div>
          )}

          <div className="mt-[25px] w-full">
            {strikeThroughPrice ? (
              <Button
                style={{ height: 42 }}
                variant="secondary"
                className={`strike-btn mx-0 flex items-center justify-center p-0 text-base`}
              >
                <img src="/assets/icons/money-dollar.svg" />
                <p className="mb-0 ml-[1px] py-0 pl-[10px] pr-[10px] text-base font-semibold underline">
                  <s>{`${tryFormatThousands(strikeThroughPrice.toFixed(2))} ${
                    data.defaultCurrency || ''
                  }`}</s>
                </p>
              </Button>
            ) : null}

            <Button
              onClick={
                paymentLoading
                  ? () => {}
                  : listType === 'energy'
                    ? () => handlePurchaseEnergyClick?.(name, price)
                    : () => handlePurchaseClick?.(price, id, listType, title)
              }
              disabled={unlocked}
              variant="orange"
              className="web-coin-shop-card-image-button flex w-full items-center"
              data-id={`webcoin_shop_${id || name}_button`}
              style={{ height: 42 }}
            >
              {paymentLoading ? (
                <div>
                  <LoadAnimateButton />
                </div>
              ) : unlocked ? (
                <p className="mb-0 text-base font-semibold text-white">
                  {t('purchased')}
                </p>
              ) : (
                <div className="flex items-center">
                  <IcoMoonIcon
                    iconName="web-coin-circle"
                    className="mr-[1px] size-4"
                  />
                  <p className="mb-0 text-base font-semibold">
                    {tryFormatThousands(price)}
                  </p>
                </div>
              )}
            </Button>
          </div>
        </div>
      </WebCoinShopBannerMobileWrapper>

      <ClickableDiv
        className="w-full rounded"
        onClick={() => setShow(true)}
        style={borderStyleRenderer(rarity)}
      >
        <div className="web-coin-shop-card-image responsive items-center p-[10px]">
          <div className="web-coin-shop-card-image-back responsive-back relative flex">
            <div
              className="web-coin-shop-card-image-back-shine-block responsive-shine-block"
              style={avatarFrameRender(true, rarity, true)}
            />
            <div
              className={`web-coin-shop-card-image-back-shine-block-image responsive-shine-block-image${
                listType === 'frame' ? '-frame' : ''
              }`}
              style={
                listType === 'energy'
                  ? {
                      backgroundImage: `url(${image})`,
                      backgroundSize: isTablet ? 'contain' : '50%',
                    }
                  : { backgroundImage: `url(${image})` }
              }
            />
          </div>

          <div className="responsive-wrapper ml-[10px]">
            <p className="mb-0 mt-0 text-xs">{title}</p>
            <div
              className="web-coin-shop-card-image-button responsive-button flex items-center"
              data-id={`webcoin_shop_${id || name}_button`}
            >
              {paymentLoading ? (
                <div>
                  <LoadAnimateButton />
                </div>
              ) : unlocked ? (
                <p className="mb-0 text-xs font-semibold text-white">
                  {t('purchased')}
                </p>
              ) : (
                <div className="flex items-center">
                  <IcoMoonIcon
                    iconName="web-coin-circle"
                    className="text-yellow800 mr-[1px] size-3"
                  />
                  <p className="text-yellow800 mb-0 text-xs font-semibold">
                    {tryFormatThousands(price)}
                  </p>
                </div>
              )}
            </div>
            {listType !== 'energy' ? (
              <div>{onCommonRender(rarity)}</div>
            ) : (
              <div>
                <p
                  className="text-text400 mb-0 text-xs font-semibold"
                  style={{ marginTop: 7 }}
                >
                  {quantity || 1} {t('left')}
                </p>
              </div>
            )}
          </div>
        </div>
      </ClickableDiv>
    </>
  );
}

export default WebCoinShopCardMobile;
